import {useCallback, useRef} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useFlags} from 'launchdarkly-react-client-sdk'
import {useAuthClient} from '../../../../../utils/use-auth-client'
import {usePaymentPreview} from '../../../../../features/CatalogGrid/hooks/usePaymentPreview'
import {useUserFeatures} from '../../../../../context/user-features-context'
import {getIsNewTLMCAccessible} from '../../../../../features/CatalogGrid/utils/productSelectionRules'
import {
  selectActiveLocation,
  selectProductConfigInfo,
  updateLocation,
} from '../../../../../app-store/PurchaseLinkSlice'
import {calculateCouponsAmountForItem} from '../../../../../utils/coupon-helpers'
import {isFirstCycleFreeRampPlan} from '../../../../../features/CatalogGrid/utils/rampPricing'

// Analog of ProposalTool's useProposalPaymentPreview.js, scoped to the single
// active location (only one location is ever shown at a time here, unlike
// the Proposal Tool's Good/Better/Best options which can all be previewed
// together) — refreshes live due-today pricing for the active location and
// writes it back into Redux so OrderSummary shows an up-to-date
// dueTodayTotal/item.dueTodayAmount.
export function usePurchaseLinkPaymentPreview() {
  const dispatch = useDispatch()
  const client = useAuthClient()
  const {previewPayment} = usePaymentPreview()
  const activeLocation = useSelector(selectActiveLocation)
  const productConfigInfo = useSelector(selectProductConfigInfo)

  const userFeatures = useUserFeatures()
  const isNewTLMCAccessible = getIsNewTLMCAccessible(userFeatures)
  const {frontendListingsBcMc} = useFlags()

  // In the multi-location (parentPay) flow, the caller can trigger a refresh
  // for one location (e.g. an item change), switch the active location, and
  // trigger another refresh before the first call's request/fallback-math
  // has resolved. Both calls target the same locationId's Redux entry, so
  // whichever resolves LAST wins the dispatch regardless of which was
  // triggered last — an older, not-yet-discounted result can overwrite a
  // newer, correctly-discounted one. Track a per-location sequence number
  // and drop any dispatch that isn't still the latest for its locationId.
  const sequenceByLocationRef = useRef({})

  const refreshPaymentPreview = useCallback(async () => {
    if (!activeLocation) return null

    const {
      locationId,
      thryvId,
      country,
      items = [],
      coupons = [],
    } = activeLocation

    const mySequence = (sequenceByLocationRef.current[locationId] ?? 0) + 1
    sequenceByLocationRef.current[locationId] = mySequence
    const isStale = () =>
      sequenceByLocationRef.current[locationId] !== mySequence
    const productCodesStr = items.map(i => i.productCode).join(',')

    // Fetches a full coupon definition for coupons saved in backend shape
    // { couponCode, couponName }. Coupons already in builder-session shape
    // (have a `code` field) are returned unchanged.
    const hydrateCoupon = async coupon => {
      if (coupon.code) return coupon
      const code = coupon.couponCode
      if (!code) return null
      try {
        const resp = await client(
          `orders/coupon/validate?couponCode=${code}&country=${country}&addonsString=${productCodesStr}`,
        )
        return resp?.isValid ? resp : null
      } catch {
        return null
      }
    }

    const needsHydration = coupons.some(c => !c.code && c.couponCode)
    let resolvedCoupons = coupons
    if (needsHydration) {
      resolvedCoupons = (await Promise.all(coupons.map(hydrateCoupon))).filter(
        Boolean,
      )
      dispatch(
        updateLocation({
          locationId,
          changes: {coupons: resolvedCoupons},
          skipDirty: true,
        }),
      )
    }

    const validCoupons = resolvedCoupons.filter(c => c.isValid)

    // EXISTING client: backend computes discounted totals via Recurly.
    if (thryvId) {
      try {
        const couponCodes = validCoupons.map(c => c.code)
        const result = await previewPayment({
          items,
          thryvId,
          country,
          coupons: couponCodes,
          productConfigInfo,
          isNewTLMCAccessible,
          frontendListingsBcMc,
        })
        if (result) {
          if (isStale()) return activeLocation
          dispatch(
            updateLocation({
              locationId,
              changes: {...result, dueTodayIncludesTax: country === 'AU'},
              skipDirty: true,
            }),
          )
          return {
            ...activeLocation,
            ...result,
            dueTodayIncludesTax: country === 'AU',
          }
        }
      } catch (err) {
        if (err.message !== 'Billing info missing on account') {
          return activeLocation
        }
        // Fall through to the client-side calculation below.
      }
    }

    // Fallback: compute dueTodayAmount per item client-side by subtracting
    // coupon discounts from unitPrice, matching the shape the API path
    // produces (used when billing info is missing on the account).
    const updatedItems = items.map(item => {
      if (item.alreadyIncluded) return {...item, dueTodayAmount: 0}
      const prodType =
        item.productType === 'ONE_TIME_FEE'
          ? 'oneTimeFee'
          : item.productType === 'APP'
          ? 'itemBasedAddon'
          : 'plan'
      const {discountAmount} = calculateCouponsAmountForItem(
        {
          code: item.productCode,
          prodType,
          currency: {unitAmount: item.unitPrice ?? 0},
          // unitPrice is already the line total (unit × quantity); the helper
          // multiplies by quantity itself, so pass 1 to avoid counting it twice.
          quantity: 1,
        },
        validCoupons,
      )
      // Ramp plans with a free first cycle owe nothing up front.
      const upfrontPrice = isFirstCycleFreeRampPlan(item.productCode)
        ? 0
        : item.unitPrice ?? 0
      return {
        ...item,
        dueTodayAmount: Math.max(upfrontPrice - discountAmount, 0),
      }
    })
    const dueTodayTotal = updatedItems.reduce(
      (sum, i) => sum + i.dueTodayAmount,
      0,
    )
    const result = {
      items: updatedItems,
      dueTodayTotal,
      dueTodayIncludesTax: false,
    }
    if (isStale()) return activeLocation
    dispatch(updateLocation({locationId, changes: result, skipDirty: true}))
    return {...activeLocation, ...result}
  }, [
    activeLocation,
    client,
    previewPayment,
    dispatch,
    productConfigInfo,
    isNewTLMCAccessible,
    frontendListingsBcMc,
  ])

  return {refreshPaymentPreview}
}
